import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes, PermissionScopes } from '../../auth/permissions';
import {
  getChannelAccountLimitsSettings,
  resetChannelAccountLimits,
  updateChannelAccountLimits,
} from '../services/channelAccountService';
import { ChannelAccountLimitsSettingsResponse } from '../types/channelAccount';
import styles from './ConnectorsPage.module.css';

interface ChannelAccountLimitsEditorProps {
  showEditor: boolean;
  onUpdated?: () => void;
}

export const ChannelAccountLimitsEditor: React.FC<ChannelAccountLimitsEditorProps> = ({
  showEditor,
  onUpdated,
}) => {
  const { t } = useTranslation(['companies', 'admin']);
  const { hasPermission } = useAuth();
  const canView = hasPermission(PermissionScopes.accountLimitsRead);
  const canManage = hasPermission(PermissionCodes.adminAccess);
  const [settings, setSettings] = React.useState<ChannelAccountLimitsSettingsResponse | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [form, setForm] = React.useState({
    maxWhatsApp: 1,
    maxInstagram: 1,
    maxWebchat: 5,
    maxTelegram: 1,
    maxMessenger: 1,
    maxTikTok: 1,
    maxVoice: 1,
    maxWhatsAppApp: 1,
    maxTikTokApp: 1,
    maxTotal: 10,
  });

  const load = React.useCallback(async () => {
    if (!canView) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getChannelAccountLimitsSettings();
      setSettings(response);
      setForm({
        maxWhatsApp: response.effective.maxWhatsApp,
        maxInstagram: response.effective.maxInstagram,
        maxWebchat: response.effective.maxWebchat,
        maxTelegram: response.effective.maxTelegram,
        maxMessenger: response.effective.maxMessenger,
        maxTikTok: response.effective.maxTikTok,
        maxVoice: response.effective.maxVoice,
        maxWhatsAppApp: response.effective.maxWhatsAppApp,
        maxTikTokApp: response.effective.maxTikTokApp,
        maxTotal: response.effective.maxTotal,
      });
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('failedToLoadChannelAccountLimits', { ns: 'companies' }));
    } finally {
      setLoading(false);
    }
  }, [canView, t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!canManage) {
      return;
    }

    setSaving(true);
    setError(null);
    setNotice(null);

    try {
      const response = await updateChannelAccountLimits({
        maxWhatsApp: form.maxWhatsApp,
        maxInstagram: form.maxInstagram,
        maxWebchat: form.maxWebchat,
        maxTelegram: form.maxTelegram,
        maxMessenger: form.maxMessenger,
        maxTikTok: form.maxTikTok,
        maxVoice: form.maxVoice,
        maxWhatsAppApp: form.maxWhatsAppApp,
        maxTikTokApp: form.maxTikTokApp,
        maxTotal: form.maxTotal,
      });
      setSettings(response);
      setNotice(t('channelAccountLimitsSaved', { ns: 'companies' }));
      onUpdated?.();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('failedToSaveChannelAccountLimits', { ns: 'companies' }));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!canManage) {
      return;
    }

    setSaving(true);
    setError(null);
    setNotice(null);

    try {
      const response = await resetChannelAccountLimits();
      setSettings(response);
      setForm({
        maxWhatsApp: response.effective.maxWhatsApp,
        maxInstagram: response.effective.maxInstagram,
        maxWebchat: response.effective.maxWebchat,
        maxTelegram: response.effective.maxTelegram,
        maxMessenger: response.effective.maxMessenger,
        maxTikTok: response.effective.maxTikTok,
        maxVoice: response.effective.maxVoice,
        maxWhatsAppApp: response.effective.maxWhatsAppApp,
        maxTikTokApp: response.effective.maxTikTokApp,
        maxTotal: response.effective.maxTotal,
      });
      setNotice(t('channelAccountLimitsResetNotice', { ns: 'companies' }));
      onUpdated?.();
    } catch (resetError) {
      setError(resetError instanceof Error ? resetError.message : t('failedToResetChannelAccountLimits', { ns: 'companies' }));
    } finally {
      setSaving(false);
    }
  };

  if (!canView || !showEditor) {
    return null;
  }

  return (
    <div className={styles.channelAccountLimitsEditor}>
      <div className={styles.channelAccountBarHead}>
        <h4>{t('channelAccountLimitsHeading', { ns: 'companies' })}</h4>
        {settings ? (
          <span className={styles.channelAccountLimits}>
            {settings.hasOverrides
              ? t('channelAccountLimitsCustom', { ns: 'companies' })
              : t('channelAccountLimitsDefaults', { ns: 'companies' })}
          </span>
        ) : null}
      </div>

      <p style={{ color: '#516173', marginTop: 0, fontSize: '0.9rem' }}>
        {t('channelAccountLimitsHelp', { ns: 'companies' })}
      </p>

      {error ? <div className="error">{error}</div> : null}
      {notice ? <div className="success">{notice}</div> : null}

      {loading ? (
        <div style={{ color: '#516173', fontSize: '0.9rem' }}>{t('loadingChannelAccountLimits', { ns: 'companies' })}</div>
      ) : (
        <>
          <div className={styles.channelAccountLimitsGrid}>
            <label>
              {t('channelAccountLimitWhatsApp', { ns: 'companies' })}
              <input
                className="text-input"
                type="number"
                min={settings?.effective.usedWhatsApp ?? 0}
                value={form.maxWhatsApp}
                onChange={event => setForm(prev => ({ ...prev, maxWhatsApp: Number(event.target.value) }))}
                disabled={saving || !canManage}
              />
              {settings ? (
                <span className={styles.channelAccountLimitUsage}>
                  {t('channelAccountLimitUsed', { ns: 'companies', used: settings.effective.usedWhatsApp })}
                </span>
              ) : null}
            </label>

            <label>
              {t('channelAccountLimitWhatsAppApp', { ns: 'companies' })}
              <input
                className="text-input"
                type="number"
                min={settings?.effective.usedWhatsAppApp ?? 0}
                value={form.maxWhatsAppApp}
                onChange={event => setForm(prev => ({ ...prev, maxWhatsAppApp: Number(event.target.value) }))}
                disabled={saving || !canManage}
              />
              {settings ? (
                <span className={styles.channelAccountLimitUsage}>
                  {t('channelAccountLimitUsed', { ns: 'companies', used: settings.effective.usedWhatsAppApp })}
                </span>
              ) : null}
            </label>

            <label>
              {t('channelAccountLimitInstagram', { ns: 'companies' })}
              <input
                className="text-input"
                type="number"
                min={settings?.effective.usedInstagram ?? 0}
                value={form.maxInstagram}
                onChange={event => setForm(prev => ({ ...prev, maxInstagram: Number(event.target.value) }))}
                disabled={saving || !canManage}
              />
              {settings ? (
                <span className={styles.channelAccountLimitUsage}>
                  {t('channelAccountLimitUsed', { ns: 'companies', used: settings.effective.usedInstagram })}
                </span>
              ) : null}
            </label>

            <label>
              {t('channelAccountLimitWebchat', { ns: 'companies' })}
              <input
                className="text-input"
                type="number"
                min={settings?.effective.usedWebchat ?? 0}
                value={form.maxWebchat}
                onChange={event => setForm(prev => ({ ...prev, maxWebchat: Number(event.target.value) }))}
                disabled={saving || !canManage}
              />
              {settings ? (
                <span className={styles.channelAccountLimitUsage}>
                  {t('channelAccountLimitUsed', { ns: 'companies', used: settings.effective.usedWebchat })}
                </span>
              ) : null}
            </label>

            <label>
              {t('channelAccountLimitTelegram', { ns: 'companies' })}
              <input
                className="text-input"
                type="number"
                min={settings?.effective.usedTelegram ?? 0}
                value={form.maxTelegram}
                onChange={event => setForm(prev => ({ ...prev, maxTelegram: Number(event.target.value) }))}
                disabled={saving || !canManage}
              />
              {settings ? (
                <span className={styles.channelAccountLimitUsage}>
                  {t('channelAccountLimitUsed', { ns: 'companies', used: settings.effective.usedTelegram })}
                </span>
              ) : null}
            </label>

            <label>
              {t('channelAccountLimitMessenger', { ns: 'companies' })}
              <input
                className="text-input"
                type="number"
                min={settings?.effective.usedMessenger ?? 0}
                value={form.maxMessenger}
                onChange={event => setForm(prev => ({ ...prev, maxMessenger: Number(event.target.value) }))}
                disabled={saving || !canManage}
              />
              {settings ? (
                <span className={styles.channelAccountLimitUsage}>
                  {t('channelAccountLimitUsed', { ns: 'companies', used: settings.effective.usedMessenger })}
                </span>
              ) : null}
            </label>

            <label>
              {t('channelAccountLimitTikTok', { ns: 'companies' })}
              <input
                className="text-input"
                type="number"
                min={settings?.effective.usedTikTok ?? 0}
                value={form.maxTikTok}
                onChange={event => setForm(prev => ({ ...prev, maxTikTok: Number(event.target.value) }))}
                disabled={saving || !canManage}
              />
              {settings ? (
                <span className={styles.channelAccountLimitUsage}>
                  {t('channelAccountLimitUsed', { ns: 'companies', used: settings.effective.usedTikTok })}
                </span>
              ) : null}
            </label>

            <label>
              {t('channelAccountLimitTikTokApp', { ns: 'companies' })}
              <input
                className="text-input"
                type="number"
                min={settings?.effective.usedTikTokApp ?? 0}
                value={form.maxTikTokApp}
                onChange={event => setForm(prev => ({ ...prev, maxTikTokApp: Number(event.target.value) }))}
                disabled={saving || !canManage}
              />
              {settings ? (
                <span className={styles.channelAccountLimitUsage}>
                  {t('channelAccountLimitUsed', { ns: 'companies', used: settings.effective.usedTikTokApp })}
                </span>
              ) : null}
            </label>

            <label>
              {t('channelAccountLimitVoice', { ns: 'companies' })}
              <input
                className="text-input"
                type="number"
                min={settings?.effective.usedVoice ?? 0}
                value={form.maxVoice}
                onChange={event => setForm(prev => ({ ...prev, maxVoice: Number(event.target.value) }))}
                disabled={saving || !canManage}
              />
              {settings ? (
                <span className={styles.channelAccountLimitUsage}>
                  {t('channelAccountLimitUsed', { ns: 'companies', used: settings.effective.usedVoice })}
                </span>
              ) : null}
            </label>

            <label>
              {t('channelAccountLimitTotal', { ns: 'companies' })}
              <input
                className="text-input"
                type="number"
                min={settings?.effective.usedTotal ?? 0}
                value={form.maxTotal}
                onChange={event => setForm(prev => ({ ...prev, maxTotal: Number(event.target.value) }))}
                disabled={saving || !canManage}
              />
              {settings ? (
                <span className={styles.channelAccountLimitUsage}>
                  {t('channelAccountLimitUsed', { ns: 'companies', used: settings.effective.usedTotal })}
                </span>
              ) : null}
            </label>
          </div>

          {canManage ? (
            <div className="company-actions-inline" style={{ marginTop: 12 }}>
              <button type="button" className="btn btn-primary" onClick={() => { void save(); }} disabled={saving}>
                {saving ? t('savingEllipsis', { ns: 'admin' }) : t('channelAccountLimitsSave', { ns: 'companies' })}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { void reset(); }}
                disabled={saving || !settings?.hasOverrides}
              >
                {t('channelAccountLimitsReset', { ns: 'companies' })}
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};
